import { useEffect, useState } from 'react';
import {
  FIELD_KIND_HINTS,
  FIELD_KIND_LABELS,
  FIELD_KINDS,
  FIELD_WIDTHS,
  FIELD_WIDTH_LABELS,
} from '@leadgab/shared';
import type { FieldKind } from '@leadgab/shared';
import { Modal } from '../Modal.tsx';
import { Select } from '../Select.tsx';
import { Field, Spinner } from '../ui.tsx';
import type { FieldRow } from '../../lib/inventory.ts';

/**
 * Declares one field of the workspace's inventory.
 *
 * The key is set once and never again: it is what stored items, filter
 * parameters, template placeholders and every standing request are keyed on, so
 * changing it would orphan all four. Renaming edits the label.
 */

export interface FieldDraft {
  key: string;
  label: string;
  kind: FieldKind;
  required: boolean;
  searchable: boolean;
  filterable: boolean;
  isTitle: boolean;
  options: string;
  width: number;
}

const EMPTY: FieldDraft = {
  key: '',
  label: '',
  kind: 'text',
  required: false,
  searchable: true,
  filterable: false,
  isTitle: false,
  options: '',
  width: 100,
};

/** "Stock number" -> "stock_number", so the key is one less thing to think about. */
function keyFromLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, 'f$1')
    .slice(0, 40);
}

interface FieldDialogProps {
  open: boolean;
  field: FieldRow | null;
  saving: boolean;
  error?: string;
  fieldErrors: Record<string, string>;
  onClose: () => void;
  onSubmit: (draft: FieldDraft) => void;
}

export function FieldDialog({
  open,
  field,
  saving,
  error,
  fieldErrors,
  onClose,
  onSubmit,
}: FieldDialogProps): React.JSX.Element {
  const [draft, setDraft] = useState<FieldDraft>(EMPTY);
  const [keyTouched, setKeyTouched] = useState(false);

  useEffect(() => {
    if (!open) return;

    setKeyTouched(field !== null);
    setDraft(
      field
        ? {
            key: field.key,
            label: field.label,
            kind: field.kind,
            required: field.required,
            searchable: field.searchable,
            filterable: field.filterable,
            isTitle: field.isTitle,
            options: field.options.join(', '),
            width: field.width,
          }
        : EMPTY,
    );
  }, [open, field]);

  const set = <K extends keyof FieldDraft>(key: K, value: FieldDraft[K]): void => {
    setDraft((previous) => ({ ...previous, [key]: value }));
  };

  const toggles: [keyof FieldDraft, string, string][] = [
    ['required', 'Required', 'An item cannot be saved without it.'],
    ['searchable', 'Searchable', 'Its values are matched by the search box.'],
    ['filterable', 'Filterable', 'Standing requests can match on it, and the API can filter by it.'],
    ['isTitle', 'Use as the title', 'Shown as the item’s name in lists and alerts.'],
  ];

  return (
    <Modal
      open={open}
      title={field ? `Edit “${field.label}”` : 'Add a field'}
      description={
        field
          ? 'The key cannot change — stored items and standing requests are keyed on it.'
          : 'Fields are yours to define. Whatever you add here becomes a column, an input on the add form, and part of what the search box matches.'
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" className="lg-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="field-form" className="lg-btn-primary" disabled={saving}>
            {saving ? <Spinner /> : null}
            {field ? 'Save changes' : 'Add field'}
          </button>
        </>
      }
    >
      <form
        id="field-form"
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(draft);
        }}
      >
        {error ? (
          <p
            className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/50 dark:text-rose-200"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <Field label="Name" htmlFor="field-label" error={fieldErrors.label}>
          <input
            id="field-label"
            className="lg-input"
            placeholder="e.g. Author, Serial number, Colour"
            value={draft.label}
            onChange={(event) => {
              const label = event.target.value;
              setDraft((previous) => ({
                ...previous,
                label,
                key: keyTouched ? previous.key : keyFromLabel(label),
              }));
            }}
          />
        </Field>

        <Field
          label="Key"
          htmlFor="field-key"
          error={fieldErrors.key}
          hint={
            field
              ? 'Fixed once the field exists.'
              : 'Used in the API and in message templates as {{key}}. Lowercase letters, digits and underscores.'
          }
        >
          <input
            id="field-key"
            className="lg-input font-mono"
            value={draft.key}
            disabled={field !== null}
            onChange={(event) => {
              setKeyTouched(true);
              set('key', event.target.value);
            }}
          />
        </Field>

        <Field
          label="Kind"
          htmlFor="field-kind"
          error={fieldErrors.kind}
          hint={FIELD_KIND_HINTS[draft.kind]}
        >
          <Select
            id="field-kind"
            value={draft.kind}
            disabled={field !== null}
            options={FIELD_KINDS.map((kind) => ({
              value: kind,
              label: FIELD_KIND_LABELS[kind],
              description: FIELD_KIND_HINTS[kind],
            }))}
            onChange={(kind) => {
              set('kind', kind);
            }}
          />
        </Field>

        <Field
          label="Width on the form"
          htmlFor="field-width"
          error={fieldErrors.width}
          hint="Fields sit side by side when their widths add up to a row — three at 33% share a line."
        >
          <Select
            id="field-width"
            value={draft.width}
            options={FIELD_WIDTHS.map((width) => ({
              value: width,
              label: FIELD_WIDTH_LABELS[width] ?? `${String(width)}%`,
            }))}
            onChange={(width) => {
              set('width', width);
            }}
          />
        </Field>

        {draft.kind === 'select' ? (
          <Field
            label="Choices"
            htmlFor="field-options"
            error={fieldErrors.options}
            hint="Separate with commas."
          >
            <input
              id="field-options"
              className="lg-input"
              placeholder="Red, Blue, Black"
              value={draft.options}
              onChange={(event) => {
                set('options', event.target.value);
              }}
            />
          </Field>
        ) : null}

        <fieldset className="grid gap-2">
          <legend className="lg-label">Behaviour</legend>

          {toggles.map(([key, label, hint]) => (
            <label key={key} className="flex cursor-pointer items-start gap-2.5 text-sm">
              <input
                type="checkbox"
                className="lg-checkbox mt-0.5"
                checked={Boolean(draft[key])}
                onChange={(event) => {
                  set(key, event.target.checked as FieldDraft[typeof key]);
                }}
              />
              <span>
                <span className="font-medium text-slate-800 dark:text-slate-100">{label}</span>
                <span className="block text-xs text-slate-500 dark:text-slate-400">{hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
      </form>
    </Modal>
  );
}
